import { useRef, useState } from 'react';
import { QUALITY_PROFILES, type QualityLevel } from '../engine/quality';
import { downloadProgress, readProgressFile } from '../save/progress';
import { useGameStore } from '../state/store';
import { VEHICLES } from '../vehicles/vehicleData';

const PAINT_COLORS = [0xe23b2e, 0x2f6fd0, 0xf5c518, 0x2fbf4a, 0xf07ab5, 0xffffff, 0x2a2f36];
const QUALITY_ORDER: readonly QualityLevel[] = ['low', 'medium', 'high'];
type Page = 'main' | 'controls' | 'vehicle' | 'parent' | 'settings' | 'data';
type ImportState = 'idle' | 'loading' | 'error';

export function PauseMenu({ onResume }: { onResume: () => void }): JSX.Element {
  const [page, setPage] = useState<Page>('main');
  const [parentAnswer, setParentAnswer] = useState('');
  const [importState, setImportState] = useState<ImportState>('idle');
  const [importMessage, setImportMessage] = useState('');
  const store = useGameStore();
  const pressTimer = useRef<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const startSecretPress = (): void => {
    pressTimer.current = window.setTimeout(() => {
      store.togglePerf();
      if ('vibrate' in navigator) navigator.vibrate([20, 40, 20]);
    }, 1200);
  };
  const cancelSecretPress = (): void => {
    if (pressTimer.current !== null) clearTimeout(pressTimer.current);
    pressTimer.current = null;
  };

  const importProgress = async (file: File): Promise<void> => {
    setImportState('loading');
    setImportMessage('SPIELSTAND WIRD GEPRÜFT …');
    try {
      const progress = await readProgressFile(file);
      const persisted = await store.replaceProgress(progress);
      if (!persisted) {
        throw new Error('Der Browser erlaubt aktuell keinen lokalen Speicher. Der Spielstand wurde nicht ersetzt.');
      }
      // MissionSystem und die laufende 3D-Welt halten eigene Laufzeitdaten.
      // Erst nach bestaetigtem Speichern neu laden, damit beide sicher aus
      // demselben importierten Stand aufgebaut werden.
      window.location.reload();
    } catch (error: unknown) {
      setImportState('error');
      setImportMessage(error instanceof Error ? error.message : 'Der Spielstand konnte nicht importiert werden.');
    }
  };

  return (
    <div className="overlay">
      <div className="card">
        <h1
          onPointerDown={startSecretPress}
          onPointerUp={cancelSecretPress}
          onPointerCancel={cancelSecretPress}
          onPointerLeave={cancelSecretPress}
          style={{ pointerEvents: 'auto', cursor: 'default' }}
        >
          {page === 'main' ? 'PAUSE' : titleFor(page)}
        </h1>

        {page === 'main' && (
          <div className="choice-row">
            <Choice icon="▶" label="Weiter" onClick={onResume} />
            <Choice icon="🎮" label="Spielen" onClick={() => setPage('controls')} />
            <Choice icon="🚙" label="Fahrzeug" onClick={() => setPage('vehicle')} />
            <Choice icon="⚙" label="Eltern" onClick={() => setPage('parent')} />
          </div>
        )}

        {page === 'controls' && (
          <>
            <h2>Spielmodus</h2>
            <div className="choice-row">
              <Choice icon="🛣️" label="Freie Fahrt" selected={store.gameMode === 'free'} onClick={() => store.setGameMode('free')} />
              <Choice icon="🅿️" label="Parken" selected={store.gameMode === 'parking'} onClick={() => store.setGameMode('parking')} />
            </div>
            <h2>Steuerung</h2>
            <div className="choice-row">
              <Choice icon="◀ ▶" label="Knöpfe" selected={store.controlMode === 'buttons'} onClick={() => store.setControlMode('buttons')} />
              <Choice icon="◉" label="Lenkrad" selected={store.controlMode === 'wheel'} onClick={() => store.setControlMode('wheel')} />
            </div>
            <BackRow onBack={() => setPage('main')} />
          </>
        )}

        {page === 'vehicle' && (
          <>
            <div className="choice-row vehicle-choices">
              {VEHICLES.map((vehicle) => (
                <Choice
                  key={vehicle.id}
                  icon={vehicle.category === 'construction' ? '🚧' : '🚙'}
                  label={vehicle.name}
                  selected={store.vehicleId === vehicle.id}
                  disabled={!store.unlockedVehicles.includes(vehicle.id)}
                  onClick={() => store.setVehicle(vehicle.id)}
                />
              ))}
            </div>
            <h2>Lackfarbe</h2>
            <div className="swatches">
              {PAINT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  aria-label={`Farbe ${color.toString(16)}`}
                  className={`swatch${store.paintColor === color ? ' selected' : ''}`}
                  style={{ background: `#${color.toString(16).padStart(6, '0')}` }}
                  onClick={() => store.setPaintColor(color)}
                />
              ))}
            </div>
            <BackRow onBack={() => setPage('main')} />
          </>
        )}

        {page === 'parent' && (
          <form className="parent-gate" onSubmit={(event) => {
            event.preventDefault();
            if (parentAnswer.trim() === '7') setPage('settings');
            setParentAnswer('');
          }}>
            <p>ELTERN: WIE VIEL IST 3 + 4?</p>
            <input inputMode="numeric" pattern="[0-9]*" value={parentAnswer} onChange={(event) => setParentAnswer(event.target.value)} aria-label="Antwort" autoFocus />
            <button type="submit" className="btn">OK</button>
            <BackRow onBack={() => setPage('main')} />
          </form>
        )}

        {page === 'settings' && (
          <>
            <h2>Grafik</h2>
            <div className="choice-row">
              {QUALITY_ORDER.map((quality) => (
                <Choice
                  key={quality}
                  icon={quality === 'low' ? '🔋' : quality === 'medium' ? '⚖' : '✨'}
                  label={QUALITY_PROFILES[quality].label}
                  selected={store.quality === quality && !store.qualityAuto}
                  onClick={() => store.setQuality(quality, false)}
                />
              ))}
            </div>
            <h2>Spiel</h2>
            <div className="choice-row">
              <Choice icon={store.vibration ? '📳' : '▱'} label={store.vibration ? 'Vibration an' : 'Vibration aus'} selected={store.vibration} onClick={store.toggleVibration} />
              <Choice icon={store.sound ? '🔊' : '🔇'} label={store.sound ? 'Ton an' : 'Ton aus'} selected={store.sound} onClick={store.toggleSound} />
              <Choice icon="💾" label="Spielstand" onClick={() => setPage('data')} />
              <Choice icon="📊" label={store.showPerf ? 'Werte an' : 'Werte aus'} selected={store.showPerf} onClick={store.togglePerf} />
            </div>
            <BackRow onBack={() => setPage('main')} />
          </>
        )}

        {page === 'data' && (
          <>
            <p className="save-summary">● {store.coins} &nbsp; ★ {store.stars} &nbsp; ✓ {Object.keys(store.missionStars).length} MISSIONEN</p>
            <div className="choice-row">
              <Choice icon="⬇" label="Export" onClick={() => downloadProgress({
                version: 1,
                coins: store.coins,
                stars: store.stars,
                collected: store.collected,
                missionStars: store.missionStars,
                unlockedVehicles: store.unlockedVehicles,
              })} />
              <Choice
                icon="⬆"
                label={importState === 'loading' ? 'Import läuft' : 'Import'}
                disabled={importState === 'loading'}
                onClick={() => fileRef.current?.click()}
              />
            </div>
            {importState !== 'idle' && (
              <p className="save-summary" role={importState === 'error' ? 'alert' : 'status'} aria-live="polite">
                {importState === 'error' ? '⚠ ' : ''}{importMessage}
              </p>
            )}
            <input
              ref={fileRef}
              className="visually-hidden"
              type="file"
              accept="application/json,.json"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void importProgress(file);
              }}
            />
            <BackRow onBack={() => setPage('settings')} />
          </>
        )}
      </div>
    </div>
  );
}

function titleFor(page: Page): string {
  if (page === 'controls') return 'STEUERUNG';
  if (page === 'vehicle') return 'FAHRZEUG';
  if (page === 'parent') return 'ELTERN';
  if (page === 'data') return 'SPIELSTAND';
  return 'EINSTELLUNGEN';
}

function BackRow({ onBack }: { onBack: () => void }): JSX.Element {
  return <div className="choice-row back-row"><Choice icon="↩" label="Zurück" onClick={onBack} /></div>;
}

interface ChoiceProps {
  icon: string;
  label: string;
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function Choice({ icon, label, selected = false, disabled = false, onClick }: ChoiceProps): JSX.Element {
  return (
    <button type="button" className={`choice${selected ? ' selected' : ''}`} onClick={onClick} aria-pressed={selected} disabled={disabled}>
      <span className="icon" aria-hidden="true">{icon}</span>
      <span>{disabled ? `🔒 ${label}` : label}</span>
    </button>
  );
}

export { PAINT_COLORS };
